import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Divider,
  IconButton,
  Drawer as MuiDrawer,
  type Theme,
  Typography,
} from '@mui/material';

interface DrawerState {
  isSubmitting: boolean;
}

const DrawerSubmittingContext = createContext<(isSubmitting: boolean) => void>(
  () => {}
);

export function DrawerSubmitting({ isSubmitting }: DrawerState) {
  const setIsSubmitting = useContext(DrawerSubmittingContext);

  useEffect(() => {
    setIsSubmitting(isSubmitting);
    return () => setIsSubmitting(false);
  }, [isSubmitting, setIsSubmitting]);

  return null;
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode | ((state: DrawerState) => ReactNode);
  width?: number;
  zIndex?: number | ((theme: Theme) => number);
}

const Drawer = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = 320,
  zIndex,
}: DrawerProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  return (
    <DrawerSubmittingContext.Provider value={setIsSubmitting}>
      <MuiDrawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={zIndex == null ? undefined : { zIndex }}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            width: { xs: '100%', sm: width },
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderTopLeftRadius: { xs: 0, sm: 16 },
            borderBottomLeftRadius: { xs: 0, sm: 16 },
          },
        }}
      >
        {title != null ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                py: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {typeof title === 'string' ? (
                  <Typography variant="h6" fontWeight={600}>
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </Box>
              <IconButton
                onClick={handleClose}
                aria-label="Close"
                size="small"
                disabled={isSubmitting}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
          </>
        ) : null}

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {children}
        </Box>

        {footer != null ? (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              {typeof footer === 'function' ? footer({ isSubmitting }) : footer}
            </Box>
          </>
        ) : null}
      </MuiDrawer>
    </DrawerSubmittingContext.Provider>
  );
};

export default Drawer;
